import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Landlords from "@/pages/Landlords";
import Properties from "@/pages/Properties";
import Inspections from "@/pages/Inspections";
import InspectionWizard from "@/pages/InspectionWizard";
import Compare from "@/pages/Compare";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminInspections from "@/pages/admin/AdminInspections";
import AdminReports from "@/pages/admin/AdminReports";
import AdminPayments from "@/pages/admin/AdminPayments";

import CustomerLayout from "@/pages/portal/CustomerLayout";
import {
  CustomerDashboard, CustomerProperties, CustomerInspectionsList,
  CustomerInspectionDetail, CustomerAccount, CustomerDocuments,
} from "@/pages/portal/CustomerPages";

import { Privacy, Terms } from "@/pages/Legal";
import CookieConsent from "@/components/CookieConsent";

import "@/App.css";

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#475569]">
      Loading Trent Property Management…
    </div>
  </div>
);

const AdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/admin-login" replace />;
  if (user.role !== "admin") return <Navigate to="/customer-dashboard" replace />;
  return children;
};

const CustomerOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin-dashboard" replace />;
  return children;
};

// /login (customer) — if a logged-in admin lands here, bounce to admin dashboard
const CustomerLoginGate = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user?.role === "admin") return <Navigate to="/admin-dashboard" replace />;
  if (user) return <Navigate to="/customer-dashboard" replace />;
  return children;
};

// /admin-login — if a logged-in customer lands here, bounce to customer dashboard
const AdminLoginGate = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user?.role === "admin") return <Navigate to="/admin-dashboard" replace />;
  if (user) return <Navigate to="/customer-dashboard" replace />;
  return children;
};

const LandingRoute = () => {
  const { loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  return <Landing />;
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingRoute />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* Auth pages */}
          <Route path="/login" element={<CustomerLoginGate><Login /></CustomerLoginGate>} />
          <Route path="/signup" element={<CustomerLoginGate><Register /></CustomerLoginGate>} />
          <Route path="/admin-login" element={<AdminLoginGate><AdminLogin /></AdminLoginGate>} />

          {/* Admin app (inspections / properties / landlords / wizard) */}
          <Route element={<AdminOnly><Layout /></AdminOnly>}>
            <Route path="/admin-dashboard" element={<Dashboard />} />
            <Route path="/admin-dashboard/landlords" element={<Landlords />} />
            <Route path="/admin-dashboard/properties" element={<Properties />} />
            <Route path="/admin-dashboard/inspections" element={<Inspections />} />
            <Route path="/admin-dashboard/inspections/new" element={<InspectionWizard />} />
            <Route path="/admin-dashboard/inspections/:id" element={<InspectionWizard />} />
            <Route path="/admin-dashboard/compare" element={<Compare />} />
          </Route>

          {/* Admin console (users, customers, payments, reports) */}
          <Route element={<AdminOnly><AdminLayout /></AdminOnly>}>
            <Route path="/admin-dashboard/console" element={<AdminOverview />} />
            <Route path="/admin-dashboard/console/users" element={<AdminUsers />} />
            <Route path="/admin-dashboard/console/customers" element={<AdminCustomers />} />
            <Route path="/admin-dashboard/console/subscriptions" element={<AdminSubscriptions />} />
            <Route path="/admin-dashboard/console/inspections" element={<AdminInspections />} />
            <Route path="/admin-dashboard/console/reports" element={<AdminReports />} />
            <Route path="/admin-dashboard/console/payments" element={<AdminPayments />} />
          </Route>

          {/* Customer portal */}
          <Route element={<CustomerOnly><CustomerLayout /></CustomerOnly>}>
            <Route path="/customer-dashboard" element={<CustomerDashboard />} />
            <Route path="/customer-dashboard/properties" element={<CustomerProperties />} />
            <Route path="/customer-dashboard/inspections" element={<CustomerInspectionsList />} />
            <Route path="/customer-dashboard/inspections/:id" element={<CustomerInspectionDetail />} />
            <Route path="/customer-dashboard/documents" element={<CustomerDocuments />} />
            <Route path="/customer-dashboard/account" element={<CustomerAccount />} />
          </Route>

          {/* Legacy path redirects for production bookmarks */}
          <Route path="/register" element={<Navigate to="/signup" replace />} />
          <Route path="/app/*" element={<Navigate to="/admin-dashboard" replace />} />
          <Route path="/admin/*" element={<Navigate to="/admin-dashboard/console" replace />} />
          <Route path="/portal/*" element={<Navigate to="/customer-dashboard" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}
